import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUnifiedLiveEnabled } from './useFeatureFlags';
import { liveCapabilitiesStore } from '../services/liveCapabilitiesStore';
import { capabilitiesSnapshot, snapshotForUser } from '../services/liveCapabilities';
import type { CapabilitiesSnapshot } from '../services/liveCapabilities';
import { subscribeAppForeground } from '../utils/appForeground';
import { trackCapabilitiesResolved } from '../services/liveCapabilityAnalytics';
import type { LiveCapabilitiesResponse } from '../types/auth';

/**
 * CORE-817 / CORE-822 — React binding for the capability store. Exposes the
 * caller's effective Live capabilities (the dynamic pre-create preview), kept
 * fresh the same way `useFeatureFlags` keeps flags fresh: one shared store, a
 * refetch on an interval and on every return to the foreground (web focus /
 * visibility, native AppState 'active' — see `subscribeAppForeground`) so a
 * level-up or a flag flip reaches an open app without a reload, even after a
 * long background sleep on mobile.
 *
 * Rights are only READ here — nothing is derived from the role or level. When
 * the `unified_live_session` flag is off or the API fails, the store yields the
 * safe baseline (never «all», never «nothing»).
 */

// Same cadence as the flags poller — a widened/narrowed grant reaches an open
// session within ~a minute without a reload.
const REFRESH_MS = 45_000;

export interface UseLiveCapabilitiesResult {
  /** The effective capability keys the caller holds right now. */
  capabilities: string[];
  /** The full response (role, level, locked list) for display/telemetry. */
  data: LiveCapabilitiesResponse;
  /**
   * True until the first load for the current user resolves. Смена аккаунта
   * поднимает его заново — синхронно, в том же рендере, где сменился `userId`:
   * визард ждёт по нему, а не по правам предыдущего пользователя.
   */
  loading: boolean;
  /**
   * Дошёл ли ответ сервера. `false` — значит подставлен safe baseline (флаг
   * выключен, пользователя нет или запрос упал), и по такому набору НЕЛЬЗЯ
   * прятать интерфейс: он совпадает с правами обычного пользователя, поэтому
   * мастеру показали бы чужой, урезанный экран.
   */
  fromServer: boolean;
  /** Force a re-fetch (e.g. after a known level-up). */
  refresh: () => void;
}

export function useLiveCapabilities(): UseLiveCapabilitiesResult {
  const { user, isLoading: authLoading } = useAuth();
  const flagEnabled = useUnifiedLiveEnabled();
  const userId = user?.id ?? null;

  // Одно состояние вместо трёх (`data` / `loading` / `fromServer`): его нельзя
  // обновить наполовину, и оно помнит, ЧЬЁ оно (`userId`).
  const [state, setState] = useState<CapabilitiesSnapshot>(() =>
    capabilitiesSnapshot(liveCapabilitiesStore, userId),
  );

  // Смена аккаунта в одной вкладке. Состояние принадлежит прошлому userId, а
  // эффект ниже подменит его только после ответа сервера — до него хук отдавал
  // права ПРЕДЫДУЩЕГО пользователя, и визард показал «Запланировать» тому, у
  // кого нет `session.schedule`. Подмена — прямо в рендере (React перерисует
  // сразу, до коммита), чтобы ни один рендер не увидел чужой набор. Не зависит
  // от `authLoading`: пока идёт вход, чужие права тем более отдавать нельзя.
  let current = state;
  if (state.userId !== userId) {
    current = snapshotForUser(state, userId, liveCapabilitiesStore);
    setState(current);
  }

  const refresh = useCallback(() => {
    void liveCapabilitiesStore.load(userId, { flagEnabled, force: true });
  }, [userId, flagEnabled]);

  useEffect(() => {
    if (authLoading) return;
    // Ответ загрузки, запущенной для прошлого userId, не должен лечь поверх
    // состояния нового: эффект уже перезапущен под него.
    let cancelled = false;

    const publish = (): CapabilitiesSnapshot => {
      const next = capabilitiesSnapshot(liveCapabilitiesStore, userId);
      setState(next);
      return next;
    };

    // Reflect store refreshes. A resolved load leaves a value in the cache →
    // publish it. An invalidation (level-up, logout) empties the cache → when
    // the flag is on, re-fetch the new set so rights widen/narrow without a
    // relogin. The `peek == null` guard keeps this from looping on our own
    // load's notification (which leaves a non-null value).
    const sync = () => {
      if (liveCapabilitiesStore.peek(userId) == null && flagEnabled && userId != null) {
        void liveCapabilitiesStore.load(userId, { flagEnabled, force: true });
        return;
      }
      const next = publish();
      // CORE-776: получение/обновление capability set. Деду́п живёт в хелпере —
      // событие уходит только когда набор реально изменился, иначе поллер
      // (каждые 45s) плодил бы тысячи строк-дублей.
      trackCapabilitiesResolved({ userId, response: next.data });
    };
    const unsubscribe = liveCapabilitiesStore.subscribe(sync);

    // Load for the CURRENT user — the key is user-scoped, so a prior account's
    // rights can never be served here. `force` so a flag flip or account switch
    // always re-fetches rather than serving a stale (possibly baseline) entry.
    void liveCapabilitiesStore
      .load(userId, { flagEnabled, force: true })
      .then(() => {
        if (cancelled) return;
        const next = publish();
        trackCapabilitiesResolved({ userId, response: next.data });
      });

    // Background refresh: an interval (frozen by the OS while backgrounded on
    // native) plus a forced reload every time the app returns to the
    // foreground — web focus/visibility, native AppState 'active'. The
    // foreground reload is the mobile half's core guarantee (CORE-822): after a
    // long background sleep the set is re-fetched in full, or — offline/error —
    // collapsed to the safe baseline by the store, so a stale wide grant never
    // survives a return to focus.
    const interval = setInterval(() => {
      void liveCapabilitiesStore.load(userId, { flagEnabled, force: true });
    }, REFRESH_MS);

    const unsubscribeForeground = subscribeAppForeground(() => {
      void liveCapabilitiesStore.load(userId, { flagEnabled, force: true });
    });

    return () => {
      cancelled = true;
      unsubscribe();
      clearInterval(interval);
      unsubscribeForeground();
    };
  }, [userId, flagEnabled, authLoading]);

  return {
    capabilities: current.data.capabilities,
    data: current.data,
    loading: current.loading,
    fromServer: current.fromServer,
    refresh,
  };
}
